/**
 * RedesignModalLayout.js
 * -------------------------------
 * Redesign contact modal (spec §5.13): resolves a CTA mode against the page
 * configs and shows or hides the parts of views/redesign/partials/contact-modal.ejs
 * that the mode and page config call for. Hidden fields are also disabled so
 * FormData leaves them out (CSS display does not).
 */

// Mode keys copied over the page-type config they build on.
const MODE_OVERRIDES = [
  'formTitle', 'submitText', 'intro', 'privacyNotice', 'successMessage',
  'responseTime', 'showRole', 'roleRequired', 'programRequired', 'clientFields'
];

/** Parses the #contact-modal-settings JSON block ({} when missing or invalid). */
export function readModalSettings(doc = document) {
  const el = doc.getElementById('contact-modal-settings');
  if (!el) return {};
  try {
    return JSON.parse(el.textContent || '{}') || {};
  } catch {
    return {};
  }
}

/** Last path segment ("/services/coaching/" → "coaching"), or "home" for "/". */
export function pageKey(pathname = '/') {
  const parts = String(pathname).split('/').filter(Boolean);
  return parts.length ? parts[parts.length - 1] : 'home';
}

/** Fills {page}, {slug} and {placement} in a mode's source template. */
export function buildSource(template, { page, slug, placement } = {}) {
  if (typeof template !== 'string' || !template) return '';
  return template
    .replace(/\{page\}/g, page || 'home')
    .replace(/\{slug\}/g, slug || page || 'home')
    .replace(/\{placement\}/g, placement || 'page');
}

/**
 * The config a CTA mode opens with: the mode's page type, else the trigger's
 * page type (`basePageType`, from data-modal-type), else the current page's
 * config, with the mode's own settings on top. Null when `modeName` is not a
 * configured mode, so callers fall back to the page-type lookup.
 */
export function resolveMode(modalConfigs = {}, settings = {}, modeName, basePageType) {
  const modes = settings.modes || {};
  if (!modeName || !Object.prototype.hasOwnProperty.call(modes, modeName)) return null;
  const mode = modes[modeName] || {};
  const fromBase = !(mode.pageType && modalConfigs[mode.pageType])
    && Boolean(basePageType && modalConfigs[basePageType]);
  const base = (mode.pageType && modalConfigs[mode.pageType])
    || (fromBase && modalConfigs[basePageType]) || modalConfigs.default || {};
  const config = { ...base };
  MODE_OVERRIDES.forEach((key) => {
    if (Object.prototype.hasOwnProperty.call(mode, key)) config[key] = mode[key];
  });
  return { modeName, mode, config, ...(fromBase && { basePageType }) };
}

/** Response time for the success message ("… will contact you within …"): mode, then page config, then the sitewide default. */
export function responseTimeFor(config = {}, settings = {}) {
  if (Object.prototype.hasOwnProperty.call(config, 'responseTime')) return config.responseTime || '';
  return settings.responseTime || '';
}

function setShown(el, shown) {
  if (!el) return;
  el.hidden = !shown;
  const controls = el.matches('input, select, textarea, fieldset') ? [el] : [];
  el.querySelectorAll('input, select, textarea').forEach((c) => controls.push(c));
  controls.forEach((c) => { c.disabled = !shown; });
}

function setRequired(form, name, required) {
  const input = form.querySelector(`[name="${name}"]`);
  if (input) input.required = !!required;
  const opt = form.querySelector(`[data-opt-for="${name}"]`);
  if (opt) opt.hidden = !!required;
}

function buildChoices(container, options) {
  const nodes = options.map((option) => {
    const label = document.createElement('label');
    label.className = 'choice';
    const input = document.createElement('input');
    input.type = 'checkbox';
    input.name = 'services';
    input.value = String(option);
    const text = document.createElement('span');
    text.textContent = String(option);
    label.append(input, text);
    return label;
  });
  container.replaceChildren(...nodes);
}

/**
 * Shows the parts of the form this config and mode call for.
 * @param {HTMLFormElement} form
 * @param {Object} config - resolved page/mode config
 * @param {Object} settings - #contact-modal-settings
 */
export function applyLayout(form, config = {}, settings = {}) {
  if (!form) return;
  const clientFields = config.clientFields !== false;
  form.querySelectorAll('[data-client-field]').forEach((el) => setShown(el, clientFields));

  // Page-config show* switches apply on top of the client-field switch.
  if (clientFields) setShown(form.querySelector('[data-field="location"]'), config.showLocation !== false);
  setShown(form.querySelector('[data-field="phone"]'), config.showPhone !== false);
  setShown(form.querySelector('[data-field="message"]'), config.showMessage !== false);

  const required = Array.isArray(config.requiredFields) ? config.requiredFields : ['firstName', 'email'];
  setRequired(form, 'lastName', required.includes('lastName'));
  setRequired(form, 'phone', config.phoneRequired === true || required.includes('phone'));
  setRequired(form, 'message', config.messageRequired === true);

  const message = form.querySelector('[name="message"]');
  if (message) message.placeholder = typeof config.messagePlaceholder === 'string' ? config.messagePlaceholder : '';

  const roleSet = form.querySelector('#contact-role-set');
  setShown(roleSet, config.showRole === true);
  if (roleSet && config.showRole !== true) {
    roleSet.querySelectorAll('input').forEach((i) => { i.checked = false; });
  }

  const intro = form.querySelector('#contact-intro');
  if (intro) {
    const text = typeof config.intro === 'string' ? config.intro : (intro.dataset.defaultIntro || '');
    intro.textContent = text;
    intro.hidden = !text;
  }

  const privacy = form.querySelector('#contact-privacy');
  if (privacy) privacy.textContent = config.privacyNotice || settings.privacyNotice || privacy.textContent;

  const servicesSet = form.querySelector('#contact-services-set');
  const choices = form.querySelector('#dynamic-checkboxes');
  const options = Array.isArray(config.checkboxOptions) ? config.checkboxOptions : [];
  if (choices) buildChoices(choices, options);
  setShown(servicesSet, options.length > 0);
  const legend = form.querySelector('#contact-services-legend');
  if (legend) legend.textContent = clientFields ? 'Service interest' : 'What you are interested in';

  const program = form.querySelector('[name="program"]');
  if (program) program.disabled = !(config.programRequired === true || program.value);
}

/** Where an inline error goes: the fieldset for a choice group, else the field's label. */
export function errorAnchor(field) {
  if (!field) return null;
  if (field.type === 'radio' || field.type === 'checkbox') return field.closest('fieldset') || field.parentNode;
  return field.closest('.f, .f-info') || field.parentNode;
}

/** Keeps Tab and Shift+Tab inside `container`. */
export function trapFocus(event, container) {
  if (event.key !== 'Tab' || !container) return;
  const focusable = Array.from(container.querySelectorAll(
    'a[href], button:not([disabled]), input:not([disabled]):not([type="hidden"]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])'
  )).filter((el) => !el.closest('[hidden], [inert]') && el.getClientRects().length > 0);
  if (!focusable.length) return;
  const first = focusable[0];
  const last = focusable[focusable.length - 1];
  if (event.shiftKey && document.activeElement === first) {
    event.preventDefault();
    last.focus();
  } else if (!event.shiftKey && document.activeElement === last) {
    event.preventDefault();
    first.focus();
  }
}
